/**
 * Drives cookie consent for the web app. Mounted at the root regardless of PostHog config, so the
 * banner and the first-party consent audit log work even with analytics off; every PostHog call is
 * guarded by `POSTHOG_KEY` (the provider only mounts with a key, so `usePostHog()` is otherwise a
 * no-op stub).
 *
 * PostHog boots cookieless + opted-out (`persistence: "memory"`, `opt_out_capturing_by_default`,
 * see `POSTHOG_OPTIONS`). This component resolves the decision:
 *   - existing cookie → re-apply it (returning visitor, no banner, no re-log)
 *   - no cookie + non-EU → capture-by-default (opt-out regime): opt in, set cookie, don't log
 *   - no cookie + EU → show the banner; the choice opts in/out, sets the shared cookie, and logs
 * On grant we upgrade persistence to `localStorage+cookie` (PostHog's default cross-subdomain cookie
 * then stitches landing↔app). A "Cookie preferences" affordance re-opens the banner to withdraw.
 */

import { useCallback, useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  fetchRegion,
  getConsent,
  logConsent,
  setConsent,
  useConsentPrefs,
  type ConsentChoice,
} from "@/lib/consent";
import { optInPostHog, optOutPostHog, POSTHOG_KEY } from "@/lib/posthog";

export function ConsentManager() {
  const posthog = usePostHog();
  const { open, setChoice, setOpen } = useConsentPrefs();
  const [showBanner, setShowBanner] = useState(false);

  // Apply an opt-in/out to PostHog (no cookie/log side effects — used for returning visitors too),
  // and publish the applied choice so usePostHogIdentify can identify once opt-in has happened.
  const apply = useCallback(
    (choice: ConsentChoice) => {
      if (POSTHOG_KEY) {
        if (choice === "granted") optInPostHog(posthog);
        else optOutPostHog(posthog);
      }
      setChoice(choice);
    },
    [posthog, setChoice]
  );

  // Persist a fresh choice: cookie + PostHog + (optionally) an audit record.
  const persist = useCallback(
    (choice: ConsentChoice, { log }: { log: boolean }) => {
      setConsent(choice);
      apply(choice);
      if (log) void logConsent(choice);
    },
    [apply]
  );

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      apply(existing); // returning visitor — honor prior choice silently
      return;
    }
    let cancelled = false;
    void fetchRegion().then((region) => {
      if (cancelled) return;
      // A decision may have landed while the region was in flight (e.g. the user opened cookie
      // preferences and chose). Re-read the cookie and honor it — never override an explicit choice
      // with the ROW implied grant.
      if (getConsent()) return;
      if (region === "row") {
        persist("granted", { log: false }); // opt-out regime: implied, no banner, no record
        // If cookie preferences opened the banner during the fetch, close it — and reset `open` too,
        // so a later openCookieSettings() (open: false → true) still re-triggers the show effect.
        setShowBanner(false);
        setOpen(false);
      } else {
        setShowBanner(true); // EU/EEA/UK/CH (or unknown): prompt
      }
    });
    return () => {
      cancelled = true;
    };
  }, [apply, persist, setOpen]);

  // A "Cookie preferences" link (see openCookieSettings) re-opens the banner to change/withdraw.
  useEffect(() => {
    if (open) setShowBanner(true);
  }, [open]);

  const close = () => {
    setShowBanner(false);
    setOpen(false);
  };
  const choose = (choice: ConsentChoice) => {
    persist(choice, { log: true });
    close();
  };

  if (!showBanner) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] max-w-sm rounded-xl border border-border bg-background/95 p-4 shadow-lg backdrop-blur-sm"
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm font-semibold text-foreground">We value your privacy</p>
        <p className="text-sm text-muted-foreground">
          We use analytics cookies to understand product usage and improve LATO. You can accept or
          decline; declining keeps analytics off. See our{" "}
          <a
            href={`${import.meta.env.VITE_LANDING_URL ?? "https://latolabs.io"}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
          >
            privacy policy
          </a>
          .
        </p>
        <div className="flex shrink-0 justify-end gap-2">
          <button
            type="button"
            onClick={() => choose("denied")}
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => choose("granted")}
            className="rounded-full bg-foreground px-4 py-1.5 text-sm font-medium text-background transition-colors hover:opacity-90"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
